import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Grid, Header, Icon, Label, List, ListContent, ListItem } from 'semantic-ui-react';
import { SafeButton } from '../common';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { getUsername } from '../auth';
import { ASSOCIATE_MANAGER_ROLE } from '../../constants/common';
import { MEMBERSHIP_KINDS, groupTeamMembers, hasRole, isLeader } from './teamMembership';

const memberKey = (member) => `${member.userId}-${member.isSecondary ? 'secondary' : 'primary'}`;

const removeButtonStyle = { width: 'fit-content', height: 'fit-content', padding: '0.5rem', margin: 0 };

const groupByPosition = (members) => {
    const groups = new Map();
    members.forEach((member) => {
        const position = member.position || 'Other';
        groups.set(position, [...(groups.get(position) || []), member]);
    });
    return [...groups];
};

const MemberEntry = ({ member, showMembership, canRemove, removing, onRemove }) => {
    const membership = member.isSecondary ? 'secondary' : 'primary';
    return (
        <div className="list-item" data-testid={`team-member-${memberKey(member)}`}>
            <ListItem>
                <ListContent>
                    <Link to={`/members/${member.userId}/view`} style={{ fontWeight: 'bold' }}>{member.name}</Link>
                    {showMembership && (
                        <Label size="tiny" basic color={member.isSecondary ? 'orange' : 'blue'} style={{ marginLeft: '0.5rem' }}>
                            {member.isSecondary ? 'Secondary' : 'Primary'}
                        </Label>
                    )}
                </ListContent>
            </ListItem>
            {canRemove && (
                <SafeButton
                    submitText={<Icon style={{ margin: 0 }} color="red" link name="close" />}
                    ariaLabel={`Remove ${member.name} (${membership})`}
                    confirmMsg={`Remove ${member.name} as a ${membership} member of this team?`}
                    submitAction={() => onRemove(member)}
                    disabled={removing}
                    color=""
                    style={removeButtonStyle}
                />
            )}
        </div>
    );
};

const MembershipRow = ({ title, emptyText, children, isEmpty }) => (
    <Grid.Row>
        <Grid.Column width={4} textAlign="right">
            <Label size="large" active pointing="right">{title}</Label>
        </Grid.Column>
        <Grid.Column width={8}>{isEmpty ? <p>{emptyText}</p> : children}</Grid.Column>
    </Grid.Row>
);

export const TeamMembershipSection = ({
    members, canEditTeam, canEditTeamMembers, canEditTeamLeadership, canEditSecondaryAm, disabled = false, onMembersChange,
}) => {
    const [activeKind, setActiveKind] = useState(null);
    const { managers, associateManagers, teamMembers } = groupTeamMembers(members);
    const username = getUsername();
    const canAddMembers = canEditTeam || canEditTeamMembers;
    const canAddSecondaryLeaders = canEditTeam || canEditSecondaryAm;
    const canAddPrimaryAm = canEditTeam || canEditTeamLeadership;

    const canRemoveMembership = (member) => {
        if (canEditTeam) return true;
        if (member.userId === username) return false;
        if (!isLeader(member)) return canEditTeamMembers;
        if (member.isSecondary) return canEditSecondaryAm;
        if (hasRole(member, ASSOCIATE_MANAGER_ROLE)) return canEditTeamLeadership;
        return false;
    };

    const canAddPrimary = (kind) => {
        if (kind.key === MEMBERSHIP_KINDS.member.key) return canAddMembers;
        if (kind.key === MEMBERSHIP_KINDS.associateManager.key) return canAddPrimaryAm;
        return canEditTeam;
    };

    const entryProps = (member) => ({
        member,
        canRemove: canRemoveMembership(member),
        removing: disabled || activeKind !== null,
        onRemove: handleRemove,
    });

    const openAddFlow = (kind) => {
        setActiveKind(kind);
    };

    const handleAdded = (updatedMembers) => {
        onMembersChange(updatedMembers);
        setActiveKind(null);
    };

    function handleRemove(member) {
        if (!canRemoveMembership(member)) return;
        onMembersChange(members.filter((m) => memberKey(m) !== memberKey(member)));
    }

    return (
        <>
            <MembershipRow title="Managers" emptyText="No managers for this team." isEmpty={managers.length === 0}>
                <List relaxed className="list" data-testid="managers-list">
                    {managers.map((manager) => <MemberEntry key={memberKey(manager)} showMembership {...entryProps(manager)} />)}
                </List>
            </MembershipRow>
            <MembershipRow
                title="Associate Managers"
                emptyText="No associate manager for this team."
                isEmpty={associateManagers.length === 0}
            >
                <List relaxed className="list" data-testid="associate-managers-list">
                    {associateManagers.map((am) => <MemberEntry key={memberKey(am)} showMembership {...entryProps(am)} />)}
                </List>
            </MembershipRow>
            <MembershipRow title="Team Members" emptyText="No members in this team." isEmpty={teamMembers.length === 0}>
                <List data-testid="members-list" relaxed className="list">
                    {groupByPosition(teamMembers).map(([position, positionMembers]) => (
                        <Fragment key={position}>
                            <List.Item>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <Header size="small" style={{ margin: 0 }}>{position}</Header>
                                    <span>({positionMembers.length} {positionMembers.length > 1 ? 'members' : 'member'})</span>
                                </div>
                            </List.Item>
                            {positionMembers.map((member) => <MemberEntry key={memberKey(member)} {...entryProps(member)} />)}
                        </Fragment>
                    ))}
                </List>
            </MembershipRow>
            {(canAddMembers || canAddSecondaryLeaders || canAddPrimaryAm) && (
                <Grid.Row centered>
                    <Grid.Column width={12}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem' }}>
                            {canAddMembers && (
                                <Button type="button" icon="add user" content={MEMBERSHIP_KINDS.member.action}
                                    disabled={disabled || activeKind !== null}
                                    onClick={() => openAddFlow(MEMBERSHIP_KINDS.member)} />
                            )}
                            {canAddSecondaryLeaders && <Button type="button" icon="add user" content={MEMBERSHIP_KINDS.manager.action}
                                disabled={disabled || activeKind !== null}
                                onClick={() => openAddFlow(MEMBERSHIP_KINDS.manager)} />}
                            {(canAddSecondaryLeaders || canAddPrimaryAm) && <Button type="button" icon="add user" content={MEMBERSHIP_KINDS.associateManager.action}
                                disabled={disabled || activeKind !== null}
                                onClick={() => openAddFlow(MEMBERSHIP_KINDS.associateManager)} />}
                        </div>
                    </Grid.Column>
                </Grid.Row>
            )}
            {activeKind && (
                <AddTeamMemberModal
                    kind={activeKind}
                    members={members}
                    canAddPrimary={canAddPrimary(activeKind)}
                    canAddSecondary={canAddSecondaryLeaders}
                    onClose={() => setActiveKind(null)}
                    onAdded={handleAdded}
                />
            )}
        </>
    );
};
